/**
 * AgreementDialog
 *
 * The commitment itself: title, sanitised body, a confirmation checkbox and the accept button.
 * Used both by the blocking gate after sign-in and by the machine-creation flow, so the wording
 * lives in one place.
 *
 * There is no read-to-the-end rule: the checkbox is available as soon as the dialog opens. The
 * rule disabled the checkbox until the body had been scrolled, and a disabled control swallows the
 * tap without saying why, so customers read it as a broken checkbox.
 *
 * The dialog cannot be dismissed — an "X" here would look like a way past a gate the backend
 * still enforces. A caller may supply a secondary action as the way out, and the machine-creation
 * flow does. The sign-in gate supplies none by choice, so there the only ways on are to agree or
 * to close the tab.
 */
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTypedTranslation } from "@/hooks/useTypedTranslation";
import { supportedLanguages, type SupportedLanguage } from "@/config/i18n";
import { NOTICE_HTML_CLASSES, toSafeNoticeHtml } from "@/lib/noticeHtml";
import type { AgreementDto } from "@/services/agreement.service";

/** How each language names itself. A reader who cannot read the current one still recognises it. */
const LANGUAGE_LABELS: Record<string, string> = {
  vi: "Tiếng Việt",
  en: "English",
};

/**
 * Opens on the console's own language when the revision was published in it.
 *
 * i18n.language can carry a region ("en-US"), so only the leading subtag is compared. Falls back
 * to the first language published rather than to a hard-coded one — the server guarantees the
 * default is present, and guessing a code that is absent would render an empty dialog.
 */
function pickInitialCulture(
  agreement: AgreementDto,
  uiLanguage: string,
): string {
  // `?? []` is not defensive noise: this runs inside useState's initialiser on the gate that
  // blocks the whole console, so a payload without translations threw here and replaced the
  // entire app with an error page. The service filters those out; this is the second line.
  const translations = agreement.translations ?? [];
  const preferred = uiLanguage?.split("-")[0]?.toLowerCase();
  const match = translations.find((entry) => entry.culture === preferred);
  return match?.culture ?? translations[0]?.culture ?? "vi";
}

interface AgreementDialogProps {
  open: boolean;
  agreement: AgreementDto;
  /**
   * Translation key of the line above the body, explaining why this is being asked now. Keys, not
   * text, here and below: the dialog renders them in its own language, not the console's.
   */
  promptKey?: string;
  isAccepting: boolean;
  errorKey?: string | null;
  /** Receives the language that was on screen, which is recorded with the acceptance. */
  onAccept: (culture: string) => void;
  /**
   * The way out, when the caller has one to offer. The machine-creation flow does (it closes its
   * step and goes back to the list); the sign-in gate deliberately does not, so the button is not
   * rendered there at all — see the note on the dialog above.
   */
  secondaryLabelKey?: string;
  onSecondary?: () => void;
}

export function AgreementDialog({
  open,
  agreement,
  promptKey,
  isAccepting,
  errorKey,
  onAccept,
  secondaryLabelKey,
  onSecondary,
}: AgreementDialogProps) {
  const { i18n } = useTypedTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Which language is on screen. Starts on the console's own language when the revision was
  // published in it, so a Vietnamese customer is not handed English to read first.
  const [culture, setCulture] = useState(() =>
    pickInitialCulture(agreement, i18n.language),
  );

  // The dialog's own strings follow the language chosen in it, so the prompt, checkbox and buttons
  // switch with the body. The console behind it and the saved preference stay as they are. A
  // language the console has no strings for leaves them in the console's language.
  const textLanguage = supportedLanguages.includes(culture as SupportedLanguage)
    ? culture
    : undefined;
  const { t } = useTranslation("common", { lng: textLanguage });

  const translations = agreement.translations ?? [];
  const shown =
    translations.find((entry) => entry.culture === culture) ?? translations[0];

  // A new revision is a new document: whatever was ticked for the previous one says nothing
  // about this one. The language chosen in the dialog stays when the new revision offers it — the
  // console does not follow that choice, so its language would undo it — and is picked again
  // when it does not.
  useEffect(() => {
    setConfirmed(false);
    setCulture((current) => {
      const stillOffered = (agreement.translations ?? []).some(
        (entry) => entry.culture === current,
      );
      if (stillOffered) {
        return current;
      }
      return pickInitialCulture(agreement, i18n.language);
    });
    // The revision's identity is what matters here, not the object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement.type, agreement.version, agreement.contentHash]);

  const handleLanguageChange = (next: string) => {
    if (next === culture) {
      return;
    }

    setCulture(next);
    // The tick goes with the language: it asserted agreement to the text that was on screen. The
    // box goes back to the top so the new text is read from its start.
    setConfirmed(false);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  return (
    <Dialog open={open}>
      <DialogContent
        // No close button, and neither Escape nor a click outside dismisses it.
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="max-h-[85vh] max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>{shown?.title}</DialogTitle>
        </DialogHeader>

        <DialogBody ref={bodyRef} className="scrollbar-thin space-y-4 text-sm">
          <div className="flex items-start justify-between gap-3">
            <p className="min-w-0 text-muted">
              {t(promptKey ?? "agreements.readPrompt")}
            </p>

            {/* Only when there is a choice to make. One language published means no switch, rather
                than a control that does nothing. */}
            {translations.length > 1 && (
              <div className="flex shrink-0 rounded-md border p-0.5">
                {translations.map((entry) => (
                  <button
                    key={entry.culture}
                    type="button"
                    onClick={() => handleLanguageChange(entry.culture)}
                    aria-pressed={entry.culture === culture}
                    className={`cursor-pointer rounded px-2 py-1 text-xs font-medium transition-colors ${
                      entry.culture === culture
                        ? "bg-brand text-white"
                        : "text-muted hover:bg-panel-2"
                    }`}
                  >
                    {LANGUAGE_LABELS[entry.culture] ??
                      entry.culture.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div
            className={NOTICE_HTML_CLASSES}
            // Same sanitiser and same allowlist the admin preview uses, so what is agreed to
            // here is what the operator saw when publishing.
            dangerouslySetInnerHTML={{
              __html: toSafeNoticeHtml(shown?.bodyHtml ?? ""),
            }}
          />
        </DialogBody>

        {errorKey && <p className="text-sm text-destructive">{t(errorKey)}</p>}

        {/* Checkbox + Label paired by id, the shape the rest of the app uses (RegisterForm,
            LoginForm). NOT a plain <label> wrapping the control: Radix renders the checkbox as a
            <button role="checkbox">, a button is not a labelable element, so wrapping it means
            clicking the sentence does nothing at all — only the 16px box responds. Radix's Label
            forwards the click to the control it names, which is what makes the whole line work. */}
        <div className="flex items-start gap-2">
          <Checkbox
            id="agreement-confirm"
            checked={confirmed}
            onCheckedChange={(value) => setConfirmed(value === true)}
            disabled={isAccepting}
            className="mt-0.5"
          />
          <Label
            htmlFor="agreement-confirm"
            className="cursor-pointer text-sm font-normal leading-snug"
          >
            {t("agreements.confirmLabel")}
          </Label>
        </div>

        <DialogFooter>
          {secondaryLabelKey && onSecondary && (
            <Button
              variant="outline"
              onClick={onSecondary}
              disabled={isAccepting}
            >
              {t(secondaryLabelKey)}
            </Button>
          )}
          <Button
            onClick={() => onAccept(culture)}
            disabled={!confirmed || isAccepting}
          >
            {isAccepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isAccepting ? t("agreements.accepting") : t("agreements.accept")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
